// packages import
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  createFilterOptions
} from '@mui/material'
import { FC, Fragment, useEffect, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
// others
import {
  Maybe,
  User,
  UserRole,
  UserType,
  useFetchAllUsersLazyQuery,
  useSearchUserLazyQuery
} from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, MATTER_LEGAL_TEAM_ROLES } from '../../../../constants'
import { assigneeDropdownStyle, flexCenter } from '../../../../theme/styleConstants'
import { badgeBgColor, titleize } from '../../../../utils'
import { CustomController } from '../../../common/CustomController'
import { reorderLegalTeam, FIXED_ROLES } from '../../../../utils/legalTeam'
import { Assignee as AssigneeType } from '../../../../interfaceTypes'
import { useLocation } from 'react-router-dom'

export const Assignee: FC = () => {
  const [allUsers, setAllUsers] = useState<Maybe<Maybe<User>[]> | undefined>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [focusedFieldRole, setFocusedFieldRole] = useState<UserRole>()
  const [caseRole, setCaseRole] = useState<{ label: string; value: UserType }>({
    label: 'Paralegal',
    value: UserType.Paralegal
  })
  const [debouncedSearchTerm] = useDebounce(setSearchTerm, 200)
  const { pathname } = useLocation()

  const {
    control,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<{
    legalTeam: AssigneeType[]
  }>()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'legalTeam'
  })

  useEffect(() => {
    if (fields.length === 0) {
      replace(FIXED_ROLES)
    } else {
      // Duplicating also creates a new matter, so it gets the create-form slotting.
      const legalTeam = reorderLegalTeam(fields, pathname.includes('create') || pathname.includes('duplicate'))
      replace(legalTeam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [fetchAllUsers, { loading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data

        if (fetchAllUsers) {
          const { users, response } = fetchAllUsers
          const { status } = response || {}

          if (status === 200 && users) {
            searchTerm ? setAllUsers(users) : setAllUsers(prevUsers => (prevUsers ? [...prevUsers, ...users] : users))
          }
        }
      }
    }
  })

  const [searchUser, { loading: searchLoader }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const {
          searchUser: { users }
        } = data || {}

        if (users && searchTerm) {
          setAllUsers(prevUsers => (prevUsers ? [...prevUsers, ...users] : users))
        }
      }
    }
  })

  // One-time prefill for Originating Attorney with "Firm Firm"
  const [prefillSearchUser] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      const users = data?.searchUser?.users || []
      const match = users?.find(user => {
        const first = (user?.firstName || '').trim().toLowerCase()
        const last = (user?.lastName || '').trim().toLowerCase()
        const full = `${first} ${last}`.trim()
        const role = user?.roles?.[0]?.role
        return full === 'firm firm' && role !== UserRole.Paralegal
      })

      // Only set if first legalTeam slot is still empty
      const current = getValues('legalTeam')?.[0]
      if (match && current && !current.userId) {
        setValue(
          'legalTeam.0',
          {
            userId: match.id || '',
            label: match.firstName ? `${match?.firstName} ${match?.lastName}` : '',
            role: UserRole.Attorney,
            userType: UserType.OriginatorAttorney,
            hourlyRate: current.hourlyRate ?? 0
          },
          { shouldValidate: true, shouldDirty: true }
        )
      }
    }
  })

  useEffect(() => {
    // Fire once on mount to try to prefill "Firm Firm"
    prefillSearchUser({
      variables: {
        searchUserInput: {
          roles: [UserRole.Attorney],
          searchTerm: 'Firm Firm'
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const autoCompleteUser = (userRole: UserRole | null) =>
    allUsers
      ?.filter(user => {
        const { roles } = user || {}
        const { role = null } = roles && roles[0] ? roles[0] : {}
        if (userRole === UserRole.Paralegal) {
          return role === userRole
        }

        return role !== UserRole.Paralegal
      })
      .map(user => {
        const { id: userId, firstName, lastName, roles } = user || {}
        const { role = null } = roles && roles[0] ? roles[0] : {}
        const label = `${`${titleize(firstName || '')} ${titleize(lastName || '')}` || ''}`
        return { userId, label, role }
      })

  useEffect(() => {
    if (!searchTerm?.trim()?.length && focusedFieldRole) {
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: 10,
              page: currentPage
            },
            roles: [focusedFieldRole]
          }
        }
      })
    }

    if (!focusedFieldRole) {
      setAllUsers([])
      setCurrentPage(1)
    }
    // eslint-disable-next-line
  }, [currentPage, searchTerm, focusedFieldRole])

  useEffect(() => {
    setAllUsers([])
    setCurrentPage(1)

    if (searchTerm && focusedFieldRole) {
      searchUser({
        variables: {
          searchUserInput: {
            roles: [focusedFieldRole],
            searchTerm
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [searchTerm])

  const handleDropdownOpen = (userRole: UserRole) => {
    setFocusedFieldRole(userRole)
    // Fetch users when dropdown opens if allUsers is empty
    if (!allUsers?.length) {
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: 10,
              page: 1
            },
            roles: [userRole]
          }
        }
      })
    }
  }

  const LegalTeamRow = (index: number, label: string, caseRole?: UserType) => {
    const isParalegal = caseRole === UserType.Paralegal
    const name = `legalTeam.${index}` as const

    return (
      <Grid container item spacing={2.5} alignItems="baseline" sx={{ position: 'relative' }}>
        <Grid item xs={12} sm={8}>
          <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error: { message } = {} } }) => {
              return (
                <Autocomplete
                  fullWidth
                  value={value}
                  options={autoCompleteUser(isParalegal ? UserRole.Paralegal : null) || []}
                  onFocus={() => {
                    const role = isParalegal
                      ? UserRole.Paralegal
                      : caseRole === UserType.Investigator
                      ? UserRole.Investigator
                      : UserRole.Attorney

                    handleDropdownOpen(role)
                  }}
                  onBlur={() => setFocusedFieldRole(undefined)}
                  loading={loading || searchLoader}
                  filterOptions={createFilterOptions({ ignoreCase: true })}
                  onChange={(event, item) => {
                    onChange({
                      ...item,
                      userType: caseRole || name,
                      flabel: label
                    })
                    setSearchTerm('')
                  }}
                  getOptionLabel={option => {
                    const { label, role, userId } = option || {}

                    if (userId) {
                      return `${label || ''} (${titleize(role || '')})`
                    } else {
                      return ''
                    }
                  }}
                  onInputChange={(event, newInputValue) => debouncedSearchTerm(newInputValue)}
                  ListboxProps={{
                    onScroll: event => {
                      const listboxNode = event.currentTarget
                      if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                        setCurrentPage(prev => prev + 1)
                      }
                    },
                    role: 'list-box',
                    style: {
                      maxHeight: '110px'
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.userId === value.userId}
                  renderOption={(props, option) => {
                    const { userId, label, role } = option || {}
                    const roleSlice = role?.slice(0, 1) || ''
                    const roleColor = role === UserRole.Admin ? role : role?.slice(0, 1) || ''

                    return (
                      <ListItem {...props} key={userId}>
                        <Box position="relative">
                          {label}
                          <Box
                            component="span"
                            sx={{
                              ...assigneeDropdownStyle,
                              ...flexCenter,
                              background: badgeBgColor(roleColor)
                            }}
                          >
                            {roleSlice?.slice(0, 1)}
                          </Box>
                        </Box>
                      </ListItem>
                    )
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={!!errors.legalTeam?.[index]?.userId}
                      helperText={errors.legalTeam?.[index]?.userId?.message || ''}
                      label={label}
                    />
                  )}
                />
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CustomController
            controllerName={`legalTeam.${index}.hourlyRate`}
            controllerLabel="Rate($/Hour)"
            fieldType="text"
            sx={{ my: 0 }}
          />
        </Grid>

        {index >= FIXED_ROLES.length && (
          <IconButton onClick={() => remove(index)} style={{ position: 'absolute', right: -42, top: 52 }}>
            <ClearIcon color="primary" />
          </IconButton>
        )}
      </Grid>
    )
  }

  const handleCaseRoleChange = (event: SelectChangeEvent<UserType>) => {
    const selected = MATTER_LEGAL_TEAM_ROLES.find(item => item.value === event.target.value)
    if (selected) {
      setCaseRole(selected)
    }
  }

  return (
    <FormControl variant="outlined" fullWidth>
      <Grid container spacing={2.5}>
        {fields.map((field, index) => (
          <Fragment key={field.id}>{LegalTeamRow(index, field.flabel || field.label, field.userType)}</Fragment>
        ))}

        <Grid item xs={12} sm={6} display="flex" alignItems="center" ml={'auto'} mt={1}>
          <FormControl fullWidth>
            <InputLabel id="case-role-select-label">Matter Role</InputLabel>
            <Select
              labelId="case-role-select-label"
              id="case-role-select"
              value={caseRole.value}
              onChange={handleCaseRoleChange}
              label="Matter Role"
            >
              {MATTER_LEGAL_TEAM_ROLES.map((item, index) => {
                return (
                  <MenuItem value={item.value} key={`${item.value}-${index}`}>
                    {item.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>

          <Button
            onClick={() =>
              append({
                userType: caseRole.value,
                userId: '',
                label: caseRole.label,
                role: null
              })
            }
            startIcon={<AddIcon />}
            variant="text"
            sx={{ fontSize: 13, justifyContent: 'flex-end', p: 0 }}
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  )
}
